import type { ReactNode } from "react";

import { formatDecimalValue, formatUSD } from "@/utils/currency";
import { abbreviateNumber } from "@/utils/helpers";
import { cn } from "@/utils/lib";

import { InfoTooltip } from "./info-tooltip";

interface CoinPriceFormatterProps {
  price: number;
  className?: string;
  maxZeroDigits?: number;
  subClassName?: string;
  maxDecimalsDigitsAfterZero?: number;
  hasTooltip?: boolean;
  abbreviatePriceThreshold?: number;
  highPriceThreshold?: number;
  lowPriceThreshold?: number;
  isUsd?: boolean;
}

const trimAndRoundToSignificantDigits = (num: number, limit = 2): number => {
  if (num === 0) return 0;

  const divisor = Math.pow(
    10,
    Math.max(0, Math.floor(Math.log10(Math.abs(num))) - limit + 1),
  );

  const rounded = Math.round(num / divisor);

  return rounded;
};

const CoinPriceFormatter = ({
  price,
  className = "",
  maxZeroDigits = 4,
  maxDecimalsDigitsAfterZero = 4,
  subClassName = "",
  hasTooltip = true,
  abbreviatePriceThreshold = 10000,
  highPriceThreshold = 1000,
  lowPriceThreshold = 1,
  isUsd = true,
}: CoinPriceFormatterProps) => {
  const isNegative = price < 0;
  const absPrice = Math.abs(price);

  if (absPrice >= abbreviatePriceThreshold) {
    return (
      <span className={cn("text-xs", className)}>
        {isNegative && "-"}
        {isUsd ? "$" : ""}
        {abbreviateNumber(absPrice)}
      </span>
    );
  }

  const formatterFunction = isUsd
    ? (price: number, numDecimals: number) =>
        formatUSD(price, true, numDecimals, "")
    : (price: number, numDecimals: number) =>
        formatDecimalValue(price, numDecimals);

  if (absPrice >= highPriceThreshold) {
    return (
      <span className={cn("text-xs", className)}>
        {isNegative && "-"}
        {formatterFunction(absPrice, 2)}
      </span>
    );
  }
  if (absPrice >= lowPriceThreshold) {
    return (
      <span className={cn("text-xs", className)}>
        {isNegative && "-"}
        {formatterFunction(absPrice, 4)}
      </span>
    );
  }
  const fixedPrice = absPrice.toFixed(20);
  const trimmed = fixedPrice.toString().replace(/0+$/, "").replace(/\.$/, "");
  const decimalIndex = trimmed.indexOf(".");
  const significantIndex = trimmed.slice(decimalIndex + 1).search(/[^0]/);
  const digitsAfterSignificantIndex = trimmed.slice(
    decimalIndex + 1 + significantIndex,
  ).length;

  const renderFormattedPrice = (limit: number) => {
    return (
      <span>
        {isNegative && "-"}
        {isUsd ? "$" : ""}
        {trimmed.slice(0, decimalIndex + 2)}
        <sub className={subClassName}>{significantIndex}</sub>
        {trimAndRoundToSignificantDigits(
          +trimmed.slice(significantIndex),
          limit,
        )
          .toString()
          .padEnd(4, "0")}
      </span>
    );
  };

  const formatPrice = (): ReactNode => {
    const maxNumAfterZerosLimit =
      digitsAfterSignificantIndex > maxDecimalsDigitsAfterZero
        ? maxDecimalsDigitsAfterZero
        : digitsAfterSignificantIndex;

    if (significantIndex > maxZeroDigits) {
      return hasTooltip ? (
        <InfoTooltip
          text={
            <span>
              {isNegative && "-"}
              {trimmed}
            </span>
          }
        >
          {renderFormattedPrice(maxNumAfterZerosLimit)}
        </InfoTooltip>
      ) : (
        renderFormattedPrice(maxNumAfterZerosLimit)
      );
    } else {
      return (
        <>
          {isNegative && "-"}
          {formatterFunction(
            +trimmed,
            significantIndex + maxNumAfterZerosLimit,
          )}
        </>
      );
    }
  };

  return (
    <div
      className={cn("coin-price inline-flex items-center text-xs", className)}
    >
      {formatPrice()}
    </div>
  );
};

export default CoinPriceFormatter;
